import type { z } from "zod";

import instance from "./";
import { looseArray, looseParse } from "./looseParse";
import {
  getOAuthTokensResponseSchema,
  oauthClientByIdResponseSchema,
  oauthClientResponseSchema,
} from "./oauth.schema";

export const OAUTH_CLIENT_STATUSES = ["ACTIVE", "DISABLED"] as const;
export type ClientStatus = (typeof OAUTH_CLIENT_STATUSES)[number];

export const OAUTH_CLIENT_CATEGORIES = [
  "SOCIAL",
  "ALGOS_AND_STRATEGIES",
  "CHARTING",
  "MARKET_DATA",
  "CRYPTO",
  "DEV_TOOLS",
] as const;
export type OAuthClientCategory = (typeof OAUTH_CLIENT_CATEGORIES)[number];

export type OAuthClientResponse = z.infer<typeof oauthClientResponseSchema>;

interface GetOAuthClientsParams {
  public?: boolean;
  featured?: boolean;
}

export const getOAuthClients = async (
  params: GetOAuthClientsParams,
): Promise<OAuthClientResponse[]> => {
  const res = await instance.get("/oauth/connect/clients", { params });
  return looseParse(
    looseArray(oauthClientResponseSchema, "getOAuthClients"),
    res.data,
    "getOAuthClients",
  );
};

export type OAuthClientByIdResponse = z.infer<
  typeof oauthClientByIdResponseSchema
>;

export const getOAuthClientById = async (
  clientId: string,
): Promise<OAuthClientByIdResponse> => {
  const res = await instance.get(`/oauth/connect/clients/${clientId}`);
  return looseParse(
    oauthClientByIdResponseSchema,
    res.data,
    "getOAuthClientById",
  );
};

export const getOAuthCreatedClients = async (): Promise<
  OAuthClientResponse[]
> => {
  const res = await instance.get(`/oauth/created_clients`);
  return looseParse(
    looseArray(oauthClientResponseSchema, "getOAuthCreatedClients"),
    res.data,
    "getOAuthCreatedClients",
  );
};

export const getOAuthConnectedClients = async (
  accountId: string,
): Promise<OAuthClientResponse[]> => {
  const res = await instance.get(`/accounts/${accountId}/oauth/clients`);
  return looseParse(
    looseArray(oauthClientResponseSchema, "getOAuthConnectedClients"),
    res.data,
    "getOAuthConnectedClients",
  );
};

export const getClientLogo = async (clientId: string): Promise<string> => {
  const res = await instance.get(`/oauth/connect/clients/${clientId}/logo`);
  return res.data;
};

export interface OAuthClientMutationRequest {
  name: string;
  redirect_uri: string[];
  description: string;
  url: string;
  terms_of_use: string;
  privacy_policy: string;
  logo: string;
  status: ClientStatus;
  screenshots: string[];
  categories: OAuthClientCategory[];
}

export interface OAuthClientCreateResponse extends OAuthClientResponse {
  client_secret: string;
}

export interface OAuthClientSecretResponse {
  client_id: string;
  client_secret: string;
}

export const createOAuthClient = async (
  payload: OAuthClientMutationRequest,
): Promise<OAuthClientCreateResponse> => {
  const res = await instance.post(`/oauth/clients`, payload);
  return res.data;
};

export const editOAuthClient = async (
  clientId: string,
  payload: OAuthClientMutationRequest,
): Promise<OAuthClientResponse> => {
  const res = await instance.put(`/oauth/clients/${clientId}`, payload);
  return res.data;
};

export const regenerateSecret = async (
  clientId: string,
): Promise<OAuthClientSecretResponse> => {
  const res = await instance.get(`/oauth/clients/${clientId}/secret`);
  return res.data;
};

export type GetOAuthTokensResponse = z.infer<
  typeof getOAuthTokensResponseSchema
>;

export const getOAuthTokens = async (
  accountId: string,
): Promise<GetOAuthTokensResponse[]> => {
  const res = await instance.get(`/accounts/${accountId}/oauth/token`);
  return looseParse(
    looseArray(getOAuthTokensResponseSchema, "getOAuthTokens"),
    [].concat(res.data).flat(),
    "getOAuthTokens",
  );
};

interface DeleteOAuthTokenData {
  client_id: string;
  owner_id: string;
}

export const deleteOAuthToken = async (
  data: DeleteOAuthTokenData,
): Promise<void> => {
  await instance.delete("/oauth/token", { data });
};

export const revokeOAuthToken = async (tokenId: string, clientId: string) => {
  const data = {
    client_id: clientId,
    token_id: tokenId,
  };
  const res = await instance.delete(`/oauth/token/${tokenId}`, { data });

  return res.data;
};

export interface OAuthAuthorizeRequest {
  response_type: string;
  client_id: string;
  redirect_uri: string;
  scope: string;
  state: string;
  live_account_id?: string;
  paper_account_id?: string;
}

export const postOAuthClient = async (
  req: OAuthAuthorizeRequest,
): Promise<OAuthClientResponse> => {
  const res = await instance.post("/oauth/client", req);
  return looseParse(oauthClientResponseSchema, res.data, "postOAuthClient");
};

export const authorize = async (req: OAuthAuthorizeRequest) => {
  const res = await instance.post("/oauth/authorize", req);
  return res.data;
};

export const token = async (req: OAuthAuthorizeRequest) => {
  const res = await instance.post("/oauth/token", req);
  return res.data;
};
